import { useState, useCallback } from 'react';
import { showToastSuccess, showToastError } from '../utils';

interface UseFormSubmitOptions<T> {
  onSubmit: (data: T) => Promise<any>;
  onSuccess?: (response: any) => void;
  onError?: (error: any) => void;
  successMessage?: string;
  errorMessage?: string;
}

interface UseFormSubmitReturn<T> {
  isSubmitting: boolean;
  error: string | null;
  submitForm: (data: T) => Promise<void>;
  resetError: () => void;
}

export function useFormSubmit<T>({
  onSubmit,
  onSuccess,
  onError,
  successMessage = 'Operation completed successfully',
  errorMessage = 'An error occurred',
}: UseFormSubmitOptions<T>): UseFormSubmitReturn<T> {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitForm = useCallback(
    async (data: T) => {
      setIsSubmitting(true);
      setError(null);

      try {
        const response = await onSubmit(data);
        
        if (response?.status === 'Success') {
          showToastSuccess(successMessage);
          onSuccess?.(response);
        } else {
          const errorMsg = response?.message || errorMessage;
          setError(errorMsg);
          showToastError(errorMsg);
          onError?.(response);
        }
      } catch (err: any) {
        const errorMsg = err?.message || errorMessage;
        setError(errorMsg);
        showToastError(errorMsg);
        onError?.(err);
      } finally {
        setIsSubmitting(false);
      }
    },
    [onSubmit, onSuccess, onError, successMessage, errorMessage]
  );

  const resetError = useCallback(() => {
    setError(null);
  }, []);

  return {
    isSubmitting,
    error,
    submitForm,
    resetError,
  };
} 
